/**
 * Painel da direita: a conversa aberta, como no WhatsApp. Cabeçalho com o
 * tipo, o vínculo, o selo "Sem marcar como lida" e o "Marcar como lida"; a
 * faixa do alerta Projetos (quando aberto); a prévia da IA numa faixa no
 * topo; as mensagens rolando sozinhas (com as novas chegando a cada 10 s) e,
 * embaixo, a caixa de escrever para quem pode responder (sem a permissão, o
 * aviso de somente leitura).
 *
 * Abrir usa a prévia da última rodada: nenhuma chamada à IA sai sozinha.
 * ESC fecha o que estiver por cima e depois a conversa (`useEscParaSair`).
 * Nada aqui marca a conversa como lida no WhatsApp: só o botão faz isso.
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { FiArrowLeft, FiEyeOff, FiFileText, FiLock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import ConfirmDialog from "../../../components/ConfirmDialog";
import { useToast } from "../../../context/ToastContext";
import { alertaDaConversa, subtituloDaConversa } from "../apresentacao";
import { FluxoDeMensagens } from "../Mensagens";
import { DialogoTirarSelo, FaixaDoAlerta, useTirarSelo } from "./AlertaProjetos";
import BaloesDeEnvio from "./BaloesDeEnvio";
import CaixaDeEnvio from "./CaixaDeEnvio";
import CartaoDaPrevia from "./CartaoDaPrevia";
import { LigarAoCliente, MenuDoTipo, SugestoesDeVinculo } from "./ControlesDaConversa";
import {
  AcoesDoCabecalho,
  AreaDasMensagens,
  BotaoCompacto,
  BotaoDeTexto,
  BotaoIcone,
  Cabecalho,
  IdentidadeDaConversa,
  LinhaDeBaixoDaConversa,
  NotaCentral,
  Osso,
  Rodape,
} from "./estilos";
import { BotaoMarcarComoLida, SeloSemMarcar } from "./LeituraNoWhatsApp";
import { AvatarDaConversa } from "./ListaDeConversas";
import useComandosDaConversa from "./useComandosDaConversa";
import useEscParaSair from "./useEscParaSair";
import useMensagensDaConversa from "./useMensagensDaConversa";
import usePrevia from "./usePrevia";

/** Perto do topo assim (px), a rolagem já busca as mensagens anteriores. */
const DISTANCIA_PARA_CARREGAR = 120;
/** Perto do fim assim (px), mensagem nova leva a rolagem junto. */
const DISTANCIA_DO_FIM = 140;

function CarregandoMensagens() {
  return (
    <div aria-label="Carregando as mensagens" style={{ display: "grid", gap: 14, paddingTop: 16 }}>
      {["48%", "62%", "36%", "54%"].map((largura, i) => (
        <div key={largura} style={{ display: "flex", justifyContent: i % 2 ? "flex-end" : "flex-start" }}>
          <Osso $altura={34} $largura={largura} style={{ borderRadius: 9 }} />
        </div>
      ))}
    </div>
  );
}

function AreaDeLeitura({ conversa, podeLer, leitura, comandos, aoResponder, aoTirarSelo }) {
  const caixa = useRef(null);
  const antesDeCarregar = useRef(null);
  const noFim = useRef(true);
  const baloesAntes = useRef(comandos.baloes.length);

  useLayoutEffect(() => {
    const el = caixa.current;
    if (!el) return;
    const mandouAgora = comandos.baloes.length > baloesAntes.current;
    baloesAntes.current = comandos.baloes.length;
    if (leitura.motivo === "anteriores" && antesDeCarregar.current) {
      el.scrollTop = el.scrollHeight - antesDeCarregar.current.altura + antesDeCarregar.current.topo;
      antesDeCarregar.current = null;
    } else if (leitura.motivo === "inicial" || mandouAgora || noFim.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [leitura.mensagens, leitura.motivo, comandos.baloes.length]);

  function anteriores() {
    const el = caixa.current;
    if (el) antesDeCarregar.current = { altura: el.scrollHeight, topo: el.scrollTop };
    leitura.carregarAnteriores();
  }

  function aoRolar(evento) {
    const el = evento.currentTarget;
    noFim.current = el.scrollHeight - el.scrollTop - el.clientHeight < DISTANCIA_DO_FIM;
    if (el.scrollTop < DISTANCIA_PARA_CARREGAR && leitura.mais && !leitura.carregandoAnteriores) anteriores();
  }

  const envios = (
    <BaloesDeEnvio baloes={comandos.baloes} aoTentarDeNovo={comandos.tentarDeNovo} aoDescartar={comandos.descartar} />
  );

  if (!podeLer) {
    return (
      <AreaDasMensagens ref={caixa} style={{ display: "grid", alignContent: "center" }}>
        <NotaCentral>
          <FiEyeOff size={14} aria-hidden="true" />
          Você vê a prévia, mas não as mensagens. Ler as conversas depende da permissão "Ler as mensagens" da Central.
        </NotaCentral>
        {comandos.baloes.length ? <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>{envios}</ol> : null}
      </AreaDasMensagens>
    );
  }

  const vazia = !leitura.carregando && !leitura.erro && leitura.mensagens.length === 0;
  return (
    <AreaDasMensagens ref={caixa} onScroll={aoRolar} aria-busy={leitura.carregando || leitura.carregandoAnteriores}>
      {leitura.carregando ? <CarregandoMensagens /> : null}
      {!leitura.carregando && leitura.mensagens.length > 0 ? (
        <>
          {leitura.carregandoAnteriores ? <NotaCentral>Carregando mensagens anteriores…</NotaCentral> : null}
          {!leitura.carregandoAnteriores && leitura.mais && !leitura.erro ? (
            <NotaCentral as="div">
              <BotaoDeTexto type="button" onClick={anteriores}>Ver mensagens anteriores</BotaoDeTexto>
            </NotaCentral>
          ) : null}
          {!leitura.mais ? (
            <NotaCentral>
              <FiLock size={12} aria-hidden="true" />
              Início das mensagens guardadas pelo monitor. CPF, RG, senhas e códigos de porta aparecem mascarados.
            </NotaCentral>
          ) : null}
          {leitura.erro ? (
            <NotaCentral role="alert" style={{ color: "var(--danger-text)" }}>
              {leitura.erro}{" "}
              <BotaoDeTexto type="button" onClick={anteriores}>Tentar de novo</BotaoDeTexto>
            </NotaCentral>
          ) : null}
          <FluxoDeMensagens
            mensagens={leitura.mensagens}
            mostrarAutores={Boolean(conversa.grupo)}
            aoResponder={aoResponder}
            alertaProjetos={alertaDaConversa(conversa)}
            aoTirarSelo={aoTirarSelo}
          >
            {envios}
          </FluxoDeMensagens>
        </>
      ) : null}
      {!leitura.carregando && leitura.erro && leitura.mensagens.length === 0 ? (
        <NotaCentral role="alert" style={{ marginTop: 24, color: "var(--danger-text)" }}>
          {leitura.erro}{" "}
          <BotaoDeTexto type="button" onClick={leitura.tentarDeNovo}>Tentar de novo</BotaoDeTexto>
        </NotaCentral>
      ) : null}
      {vazia ? (
        <>
          <NotaCentral style={{ marginTop: 24 }}>Nenhuma mensagem guardada nesta conversa ainda.</NotaCentral>
          {comandos.baloes.length ? <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>{envios}</ol> : null}
        </>
      ) : null}
      {leitura.erroDasNovas || comandos.erroDaFila ? (
        <NotaCentral role="status">{leitura.erroDasNovas || comandos.erroDaFila} A tela tenta de novo sozinha.</NotaCentral>
      ) : null}
    </AreaDasMensagens>
  );
}

/**
 * `aoFechar`: volta para a lista (ESC); `aoVoltar`: o mesmo pelo botão do
 * cabeçalho, que só aparece no celular.
 */
export default function PainelDaConversa({
  conversa,
  podeTratar,
  podeLer,
  podeResponder,
  aoVoltar,
  aoFechar,
  aoAtualizarItem,
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const previa = usePrevia(conversa, aoAtualizarItem);
  const leitura = useMensagensDaConversa(conversa.id, podeLer);
  const tirarSelo = useTirarSelo({ trocarMensagem: leitura.trocarMensagem, aoAtualizarItem });
  const [citando, setCitando] = useState(null);
  const [comAudio, setComAudio] = useState(false);
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);
  const comandos = useComandosDaConversa(conversa.id, {
    habilitado: podeResponder,
    mensagens: leitura.mensagens,
    aoConcluir: leitura.atualizar,
    aoMarcarLida: () => toast.success("Pronto: a conversa ficou como lida no WhatsApp."),
  });
  const subtitulo = subtituloDaConversa(conversa);
  const cancelarCitacao = useCallback(() => setCitando(null), []);
  useEscParaSair({
    ativo: Boolean(aoFechar),
    citando: Boolean(citando),
    aoCancelarCitacao: cancelarCitacao,
    comAudio,
    aoConfirmarSaida: () => setConfirmandoSaida(true),
    aoSair: aoFechar,
  });

  return (
    <>
      <Cabecalho>
        {aoVoltar ? (
          <BotaoIcone
            type="button"
            aria-label="Voltar para a lista"
            title="Voltar"
            onClick={comAudio ? () => setConfirmandoSaida(true) : aoVoltar}
          >
            <FiArrowLeft size={20} aria-hidden="true" />
          </BotaoIcone>
        ) : null}
        <AvatarDaConversa conversa={conversa} tamanho={40} className="cp-avatar-do-cabecalho" />
        <IdentidadeDaConversa>
          <h2 title={conversa.nome}>{conversa.nome}</h2>
          <LinhaDeBaixoDaConversa>
            <SeloSemMarcar podeMarcar={podeResponder} />
            <p title={subtitulo}>{subtitulo}</p>
          </LinhaDeBaixoDaConversa>
        </IdentidadeDaConversa>
        <AcoesDoCabecalho>
          {podeTratar ? <MenuDoTipo conversa={conversa} aoMudar={aoAtualizarItem} /> : null}
          {podeTratar ? <LigarAoCliente conversa={conversa} aoMudar={aoAtualizarItem} /> : null}
          {conversa.numero_pedido ? (
            <BotaoCompacto
              type="button"
              aria-label={`Ver a ficha do pedido ${conversa.numero_pedido}`}
              title="Ver a ficha do cliente"
              onClick={() => navigate(`/central-projetos/clientes/${encodeURIComponent(conversa.numero_pedido)}`)}
            >
              <FiFileText size={15} aria-hidden="true" />
              <span className="rotulo rotulo-opcional">Ver ficha</span>
            </BotaoCompacto>
          ) : null}
          {podeResponder ? (
            <BotaoMarcarComoLida conversa={conversa} leitura={comandos.leitura} aoPedir={comandos.recarregar} />
          ) : null}
        </AcoesDoCabecalho>
      </Cabecalho>
      <FaixaDoAlerta conversa={conversa} podeTratar={podeTratar} aoAtualizarItem={aoAtualizarItem} />
      {podeTratar ? <SugestoesDeVinculo conversa={conversa} aoMudar={aoAtualizarItem} /> : null}
      <CartaoDaPrevia conversa={conversa} estado={previa.estado} gemini={previa.gemini} aoAtualizar={previa.gerar} />
      <AreaDeLeitura
        conversa={conversa}
        podeLer={podeLer}
        leitura={leitura}
        comandos={comandos}
        aoResponder={podeResponder ? setCitando : undefined}
        aoTirarSelo={podeTratar ? tirarSelo.pedir : undefined}
      />
      {podeTratar ? <DialogoTirarSelo {...tirarSelo} /> : null}
      {podeResponder ? (
        <CaixaDeEnvio
          conversa={conversa}
          citando={citando}
          aoCancelarCitacao={cancelarCitacao}
          aoEnviar={comandos.enviar}
          aoMudarAudio={setComAudio}
        />
      ) : (
        <Rodape>
          <FiLock size={13} aria-hidden="true" />
          Somente leitura: não envia nem marca como lida
        </Rodape>
      )}
      <ConfirmDialog
        open={confirmandoSaida}
        titulo="Sair e descartar o áudio?"
        descricao="O áudio desta conversa ainda não foi mandado e se perde ao sair. O texto escrito fica guardado."
        confirmarLabel="Descartar e sair"
        onConfirmar={() => {
          setConfirmandoSaida(false);
          (aoFechar || aoVoltar)?.();
        }}
        onCancelar={() => setConfirmandoSaida(false)}
      />
    </>
  );
}
